/**
 * Admin page: Service limits and fee settings.
 * Single place to configure daily limits and per-use fees (agreement, extraction, campaign).
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  TextField,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';

interface CampaignPricing {
  emailCostCents?: number;
  smsCostCents?: number;
  agreementGenerationCostCents?: number;
  maxConsultationReportGenerationsPerAgencyPerDay?: number;
  maxConsultationReportExtractionsPerUserPerDay?: number;
  consultationReportExtractionOverLimitCostCents?: number;
  maxDescriptionGenerationsPerUserPerDay?: number;
  descriptionGenerationOverLimitCostCents?: number;
  freeAgreementGenerationsPerUserPerMonth?: number;
}

interface SystemSettings {
  smsEnabled?: boolean;
  campaignPricing?: CampaignPricing;
  [key: string]: unknown;
}

const defaultPricing: CampaignPricing = {
  emailCostCents: 0,
  smsCostCents: 0,
  agreementGenerationCostCents: 898,
  maxConsultationReportGenerationsPerAgencyPerDay: 50,
  maxConsultationReportExtractionsPerUserPerDay: 2,
  consultationReportExtractionOverLimitCostCents: 98,
  maxDescriptionGenerationsPerUserPerDay: 2,
  descriptionGenerationOverLimitCostCents: 98,
  freeAgreementGenerationsPerUserPerMonth: 1,
};

type PricingField = keyof CampaignPricing;

const AdminServiceLimitsFees = () => {
  const [settings, setSettings] = useState<SystemSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/admin/settings', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch settings');
      const data = await response.json();
      setSettings(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handlePricingChange = (field: PricingField) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = Number(event.target.value);
    setSettings((prev) => ({
      ...prev,
      campaignPricing: {
        ...(prev.campaignPricing || defaultPricing),
        [field]: Number.isFinite(value) ? value : 0,
      },
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      setSaving(true);
      const response = await fetch('/api/admin/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify(settings),
      });
      if (!response.ok) throw new Error('Failed to save settings');
      setSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const pricing = settings.campaignPricing || defaultPricing;
  const smsEnabled = (settings.smsEnabled ?? true) as boolean;

  if (loading) {
    return (
      <Container>
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography>Loading...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Service limits &amp; fees
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          Configure daily limits and per-use fees. Charges use agency campaign credits where applicable.
        </Typography>

        <Paper sx={{ p: 3 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* SMS service toggle */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  SMS service
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={smsEnabled}
                      onChange={(e) =>
                        setSettings((prev) => ({
                          ...prev,
                          smsEnabled: e.target.checked,
                        }))
                      }
                      color="primary"
                    />
                  }
                  label="SMS sending enabled"
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, ml: 4 }}>
                  When disabled, SMS campaigns and contract SMS messages are blocked, and SMS buttons in the app are disabled.
                </Typography>
              </Grid>
              {/* Service limits */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Service limits
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Agreement generation – free per user per month"
                      type="number"
                      value={pricing.freeAgreementGenerationsPerUserPerMonth ?? 1}
                      onChange={handlePricingChange('freeAgreementGenerationsPerUserPerMonth')}
                      inputProps={{ min: 0 }}
                      helperText="Free agency agreement generations per user per calendar month. After this, the cost below is charged from agency credits."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Extract RP Data – daily limit per user"
                      type="number"
                      value={pricing.maxConsultationReportExtractionsPerUserPerDay ?? pricing.maxConsultationReportGenerationsPerAgencyPerDay ?? 2}
                      onChange={handlePricingChange('maxConsultationReportExtractionsPerUserPerDay')}
                      inputProps={{ min: 0 }}
                      helperText="Max AI extractions (valuation/CMA) per user per day. 0 = no limit. Manual comparable sales always allowed."
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Description AI – daily limit per user"
                      type="number"
                      value={pricing.maxDescriptionGenerationsPerUserPerDay ?? 2}
                      onChange={handlePricingChange('maxDescriptionGenerationsPerUserPerDay')}
                      inputProps={{ min: 0 }}
                      helperText="Max Description AI generations per user per day. 0 = no limit."
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Service fees (cents) */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom color="primary">
                  Service fees (cents)
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Generate Agreement – cost (cents)"
                      type="number"
                      value={pricing.agreementGenerationCostCents ?? 898}
                      onChange={handlePricingChange('agreementGenerationCostCents')}
                      inputProps={{ min: 0 }}
                      helperText="Per agreement generation after free credits. e.g. 898 = $8.98"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Extract RP Data – over-limit cost (cents)"
                      type="number"
                      value={pricing.consultationReportExtractionOverLimitCostCents ?? 98}
                      onChange={handlePricingChange('consultationReportExtractionOverLimitCostCents')}
                      inputProps={{ min: 0 }}
                      helperText="Per extra AI extraction when user is over daily limit. e.g. 98 = $0.98"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Description AI – over-limit cost (cents)"
                      type="number"
                      value={pricing.descriptionGenerationOverLimitCostCents ?? 98}
                      onChange={handlePricingChange('descriptionGenerationOverLimitCostCents')}
                      inputProps={{ min: 0 }}
                      helperText="Per extra Description AI generation when user is over daily limit. e.g. 98 = $0.98"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email – cost per recipient (cents)"
                      type="number"
                      value={pricing.emailCostCents ?? 0}
                      onChange={handlePricingChange('emailCostCents')}
                      inputProps={{ min: 0 }}
                      helperText="Charged per email recipient (campaigns)"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="SMS – cost per segment (cents)"
                      type="number"
                      value={pricing.smsCostCents ?? 0}
                      onChange={handlePricingChange('smsCostCents')}
                      inputProps={{ min: 0 }}
                      helperText="Per SMS segment (160 chars ≈ 1 segment)"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </Paper>

        <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError(null)}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Snackbar>
        <Snackbar open={success} autoHideDuration={6000} onClose={() => setSuccess(false)}>
          <Alert severity="success" onClose={() => setSuccess(false)}>
            Settings saved.
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default AdminServiceLimitsFees;
